import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import {
  computeAll,
  round2,
  kmStandVon,
  offenerAusflugDavor,
} from '@baba/engine';
import type { FotoFeld } from '@baba/engine/types';
import type {
  Entry,
  Mitfahrer,
  Person,
  Settings,
  TripEntry,
} from '@baba/engine/types';

import { api, ApiFehler, holeIch, holeWort, setzeIch, setzeWort } from './api.ts';
import type { Zustand } from './api.ts';
import { ART_FARBE, ART_TITEL, NAME } from './format.ts';
import { Startseite } from './views/Startseite.tsx';
import { Historie } from './views/Historie.tsx';
import type { Filter } from './views/Historie.tsx';
import { Statistik } from './views/Statistik.tsx';
import { EntryForm } from './views/EntryForm.tsx';

type Tab = 'start' | 'historie' | 'statistik';

/** Was gerade über der Seite liegt: nichts, ein Formular oder ein Detail. */
type Schicht =
  | { art: 'keine' }
  | { art: 'neu'; typ: Entry['type'] }
  | { art: 'starten' }
  | { art: 'stoppen'; id: string }
  | { art: 'bearbeiten'; id: string }
  | { art: 'detail'; id: string };

const ARTEN: Entry['type'][] = ['trip', 'refuel', 'repair', 'settlement'];

export function App() {
  const [wort, setWort] = useState<string | null>(holeWort());
  const [ich, setIch] = useState<Person>(holeIch());
  const [zustand, setZustand] = useState<Zustand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [tab, setTab] = useState<Tab>('start');
  /*
    Rückfrage, ob ein vergessener Ausflug mit diesem Zählerstand geschlossen
    werden soll (R-26). Bei einem Ausflug passiert das ohne Frage - dort ist
    der Anfangsstand des nächsten zwangsläufig das Ende des vorigen. Bei
    Tanken und Reparatur ist es eine Vermutung, also wird gefragt.
  */
  const [frage, setFrage] = useState<{
    ausflug: TripEntry;
    kmStand: number;
  } | null>(null);
  const [schicht, setSchicht] = useState<Schicht>({ art: 'keine' });
  const [menuOffen, setMenuOffen] = useState(false);
  // Gehören zur Historie, überleben hier aber das Öffnen eines Eintrags.
  const [filter, setFilter] = useState<Filter>('alle');
  const [suche, setSuche] = useState('');

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      setZustand(await api.ladeZustand());
      setFehler(null);
    } catch (e) {
      if (e instanceof ApiFehler && e.status === 401) {
        setzeWort(null);
        setWort(null);
      } else {
        setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler.');
      }
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    if (wort) void laden();
  }, [wort, laden]);

  // Solange das Blatt aufliegt, scrollt die Seite darunter nicht mit weg.
  useEffect(() => {
    if (!menuOffen) return;
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = vorher;
    };
  }, [menuOffen]);

  const ergebnis = useMemo(() => {
    if (!zustand) return null;
    return computeAll(zustand.entries, zustand.settings);
  }, [zustand]);

  /**
   * Fehlermeldung.
   *
   * Muss in JEDEM Zweig stehen: App steigt für Formular und Detail früh aus,
   * und vorher lag die Meldung nur im Hauptzweig. Ein fehlgeschlagenes
   * Speichern blieb dadurch stumm - sichtbar wurde die Meldung erst, wenn man
   * das Formular schloss und auf der Hauptseite landete.
   */
  const meldung = fehler ? (
    <p
      className="meldung meldung--seite"
      role="alert"
      onClick={() => setFehler(null)}
    >
      {fehler}
    </p>
  ) : null;

  /*
    Die Rückfrage aus R-26. Sie liegt über der Seite, weil sie nach dem
    Speichern kommt - da ist das Formular schon zu, und ein Hinweis irgendwo
    in der Liste würde übersehen.
  */
  const rueckfrage = frage ? (
    <div className="rueckfrage" role="dialog" aria-modal="true">
      <div className="tafel rueckfrage__blatt">
        <p className="etikett">Ausflug noch offen</p>
        <p>
          Der Ausflug vom {datum(frage.ausflug.datum)} wurde nie gestoppt. Soll{' '}
          <strong>{km(frage.kmStand)}</strong> als Zählerstand beim Ankommen
          übernommen werden?
        </p>
        <p className="hinweis">
          Das ergibt {km(frage.kmStand - (frage.ausflug.kmStart ?? 0))}{' '}
          gefahrene Kilometer. Der Wert wird als übernommen gekennzeichnet und
          lässt sich jederzeit ändern.
        </p>
        <div className="knopfreihe">
          <button className="knopf knopf--leise" onClick={() => setFrage(null)}>
            Nein, offen lassen
          </button>
          <button
            className="knopf"
            onClick={() => {
              const f = frage;
              setFrage(null);
              void mitFehler(() => ausflugSchliessen(f.ausflug, f.kmStand));
            }}
          >
            Ja, übernehmen
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (!wort) {
    return <Anmeldung aufFertig={(w) => setWort(w)} />;
  }

  if (!zustand || !ergebnis) {
    return (
      <div className="tor">
        <p className="tor__zeile">{fehler ?? 'Einen Moment …'}</p>
        {fehler && (
          <button className="knopf" onClick={() => void laden()}>
            Nochmal versuchen
          </button>
        )}
      </div>
    );
  }

  async function mitFehler(fn: () => Promise<void>) {
    try {
      await fn();
      await laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    }
  }

  /**
   * Schliesst einen vergessenen Ausflug mit dem Stand des neuen Vorgangs.
   *
   * Er bekommt das Kennzeichen `kmEndeUebernommen` und steht damit in der
   * Detailansicht als übernommen da - der Wert ist abgeleitet, nicht abgelesen.
   */
  async function ausflugSchliessen(ausflug: TripEntry, kmStand: number) {
    await api.aendere(ausflug.id, {
      kmEnde: kmStand,
      status: 'beendet',
      kmEndeUebernommen: true,
    } as Partial<Entry>);
  }

  async function speichern(
    eingabe: Partial<Entry>,
    fotos: { datei: File; feld: FotoFeld }[],
  ) {
    await mitFehler(async () => {
      let id = eingabe.id;
      let gespeichert: Entry | undefined;
      if (schicht.art === 'bearbeiten' || schicht.art === 'stoppen') {
        id = schicht.id;
        await api.aendere(id, eingabe);
      } else {
        gespeichert = (await api.legeAn(eingabe)).entry;
        id = gespeichert.id;
      }
      /*
        Der Vorgang ist an dieser Stelle schon gespeichert. Geht ein Foto
        schief, darf das nicht wie ein misslungenes Speichern aussehen -
        sonst legt man denselben Ausflug ein zweites Mal an. Deshalb wird der
        Fehler gesammelt und am Ende benannt.
      */
      const missglueckt: string[] = [];
      for (const foto of fotos) {
        try {
          await api.ladeFotoHoch(id, foto.feld, foto.datei);
        } catch {
          missglueckt.push(fotoTitel(foto.feld));
        }
      }
      if (missglueckt.length > 0) {
        setFehler(
          `Gespeichert – aber ${missglueckt.join(' und ')} konnte nicht hochgeladen werden.`,
        );
      }

      /*
        Ein neuer Vorgang weiss, wo das Auto steht - und schliesst damit einen
        Ausflug, dessen Stoppen vergessen wurde (R-26). Nur beim Anlegen: beim
        Bearbeiten eines alten Vorgangs wäre der Vorschlag aus der Zeit
        gefallen.
      */
      if (gespeichert) {
        const offen = offenerAusflugDavor(entries, {
          id: gespeichert.id,
          datum: gespeichert.datum,
          seq: gespeichert.seq,
          kmStand: kmStandVon(gespeichert),
        });
        const stand = kmStandVon(gespeichert);
        if (offen && stand != null) {
          if (gespeichert.type === 'trip') await ausflugSchliessen(offen, stand);
          else setFrage({ ausflug: offen, kmStand: stand });
        }
      }
      /*
        Wer aus der Detailansicht heraus bearbeitet, will das Ergebnis sehen
        und nicht zurück auf die Startseite geworfen werden. Beim Anlegen und
        beim Losfahren ist der Weg zurück richtig - dort kam man nicht von
        einem einzelnen Vorgang her.
      */
      setSchicht(
        schicht.art === 'bearbeiten' || schicht.art === 'stoppen'
          ? { art: 'detail', id: id! }
          : { art: 'keine' },
      );
    });
  }

  const entries = zustand.entries;
  const settings = zustand.settings;

  // -------------------------------------------------------------------------

  if (
    schicht.art === 'neu' ||
    schicht.art === 'bearbeiten' ||
    schicht.art === 'starten' ||
    schicht.art === 'stoppen'
  ) {
    const vorhanden =
      schicht.art === 'bearbeiten' || schicht.art === 'stoppen'
        ? entries.find((e) => e.id === schicht.id)
        : undefined;
    const modus =
      schicht.art === 'starten'
        ? 'start'
        : schicht.art === 'stoppen'
          ? 'stopp'
          : 'voll';
    return (
      <div className="rahmen">
        {meldung}
        {rueckfrage}
        <main className="inhalt inhalt--formular">
          <EntryForm
            art={
              schicht.art === 'neu'
                ? schicht.typ
                : schicht.art === 'starten'
                  ? 'trip'
                  : vorhanden!.type
            }
            modus={modus}
            vorhanden={vorhanden}
            entries={entries}
            settings={settings}
            ich={ich}
            aufSpeichern={speichern}
            aufAbbrechen={() => setSchicht({ art: 'keine' })}
          />
        </main>
      </div>
    );
  }

  if (schicht.art === 'detail') {
    const eintrag = entries.find((e) => e.id === schicht.id);
    if (!eintrag) {
      setSchicht({ art: 'keine' });
      return null;
    }
    return (
      <div className="rahmen">
        {meldung}
        {rueckfrage}
        <Detail
          eintrag={eintrag}
          ergebnis={ergebnis}
          fotos={zustand.fotos.filter((f) => f.entryId === eintrag.id)}
          aufBearbeiten={() => setSchicht({ art: 'bearbeiten', id: eintrag.id })}
          aufStoppen={() => setSchicht({ art: 'stoppen', id: eintrag.id })}
          aufZurueck={() => setSchicht({ art: 'keine' })}
          aufAktion={mitFehler}
        />
      </div>
    );
  }

  return (
    <div className="rahmen">
      <header className="kopf">
        <h1 className="marke">BABA BIG</h1>
        <button
          className="wer-ich-bin"
          onClick={() => {
            const neu: Person = ich === 'michi' ? 'otto' : 'michi';
            setzeIch(neu);
            setIch(neu);
          }}
          title="Wer bist du auf diesem Gerät?"
        >
          {NAME[ich]}
        </button>
      </header>

      {rueckfrage}

      {fehler && (
        <p className="meldung" role="alert" onClick={() => setFehler(null)}>
          {fehler}
        </p>
      )}

      <main className={laedt ? 'inhalt laedt' : 'inhalt'}>
        {tab === 'start' && (
          <Startseite
            ergebnis={ergebnis}
            settings={settings}
            aufSettings={async (s: Settings) => {
              await mitFehler(async () => {
                await api.speichereSettings(s);
              });
            }}
          />
        )}
        {tab === 'historie' && (
          <Historie
            entries={entries}
            ergebnis={ergebnis}
            aufDetail={(id) => setSchicht({ art: 'detail', id })}
            filter={filter}
            aufFilter={setFilter}
            suche={suche}
            aufSuche={setSuche}
          />
        )}
        {tab === 'statistik' && <Statistik ergebnis={ergebnis} />}
      </main>

      {menuOffen && (
        <div className="schleier" onClick={() => setMenuOffen(false)}>
          <div className="blatt" onClick={(e) => e.stopPropagation()}>
            <div className="blatt__kopf">
              <span className="blatt__titel">Was ist passiert?</span>
              <button className="schliessen" onClick={() => setMenuOffen(false)}>
                ×
              </button>
            </div>
            <div className="artwahl">
              <button
                className="artwahl--betont"
                onClick={() => {
                  setMenuOffen(false);
                  setSchicht({ art: 'starten' });
                }}
              >
                <span
                  className="artwahl__punkt"
                  style={{ background: ART_FARBE.trip }}
                />
                Ausflug starten
              </button>
              {ARTEN.map((typ) => (
                <button
                  key={typ}
                  onClick={() => {
                    setMenuOffen(false);
                    setSchicht({ art: 'neu', typ });
                  }}
                >
                  <span
                    className="artwahl__punkt"
                    style={{ background: ART_FARBE[typ] }}
                  />
                  {ART_TITEL[typ]}
                </button>
              ))}
            </div>
            <button
              className="knopf knopf--leise"
              onClick={() => void api.ladeCsvHerunter()}
            >
              Alles als CSV laden
            </button>
          </div>
        </div>
      )}

      <button
        className="anlegen"
        onClick={() => setMenuOffen(true)}
        aria-label="Neuen Vorgang anlegen"
      >
        +
      </button>

      <nav className="tableiste">
        <div className="tableiste__innen">
          {(
            [
              ['start', 'Start'],
              ['historie', 'Historie'],
              ['statistik', 'Statistik'],
            ] as [Tab, string][]
          ).map(([wert, titel]) => (
            <button
              key={wert}
              className="tab"
              aria-current={tab === wert ? 'page' : undefined}
              onClick={() => setTab(wert)}
            >
              {titel}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Anmeldung({ aufFertig }: { aufFertig: (wort: string) => void }) {
  const [wort, setWort] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function absenden() {
    setLaeuft(true);
    try {
      await api.anmelden(wort);
      aufFertig(wort);
    } catch {
      setFehler('Das Wort stimmt nicht.');
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="tor">
      <h1 className="tor__marke">BABA BIG</h1>
      <p className="tor__zeile">Gemeinsames Zugangswort</p>
      <div className="feld">
        <input
          type="password"
          value={wort}
          autoFocus
          onChange={(e) => setWort(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void absenden();
          }}
        />
      </div>
      {fehler && <p className="hinweis hinweis--warnung">{fehler}</p>}
      <button className="knopf" disabled={!wort || laeuft} onClick={() => void absenden()}>
        {laeuft ? 'Moment …' : 'Rein damit'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

import type { Ergebnis, TripResult } from '@baba/engine/types';
import type { FotoInfo } from './api.ts';
import { datum, euro, zahl, km, zuText, zuZahl, WER_UNTERWEGS } from './format.ts';

export function Detail({
  eintrag,
  ergebnis,
  fotos,
  aufBearbeiten,
  aufStoppen,
  aufZurueck,
  aufAktion,
}: {
  eintrag: Entry;
  ergebnis: Ergebnis;
  fotos: FotoInfo[];
  aufBearbeiten: () => void;
  /** Nur bei einem laufenden Ausflug belegt (R-23). */
  aufStoppen?: () => void;
  aufZurueck: () => void;
  aufAktion: (fn: () => Promise<void>) => Promise<void>;
}) {
  /*
    Ergebnis eines Ausflugs - aber nur, wenn er auch eines hat.
    Ein laufender Ausflug (R-23) liefert überall Nullen. Sie anzuzeigen wäre
    schlimmer als sie wegzulassen: "0,00 € hin und zurück" liest sich wie ein
    Ergebnis, ist aber bloss ein noch nicht gefülltes Feld.
  */
  const r =
    eintrag.type === 'trip' && eintrag.status !== 'laufend'
      ? ergebnis.trips.get(eintrag.id)
      : undefined;

  /** Wer das Parken ausgelegt hat, sofern namentlich bekannt. */
  const zahlerInListe =
    eintrag.type === 'trip' && eintrag.parkgebuehrMitfahrerId != null
      ? eintrag.mitfahrer?.find(
          (m) => m.id === eintrag.parkgebuehrMitfahrerId,
        )
      : undefined;

  return (
    <>
      <header className="kopf">
        <button className="schliessen" onClick={aufZurueck}>
          ‹ Zurück
        </button>
        <button className="knopf knopf--leise" onClick={aufBearbeiten}>
          Bearbeiten
        </button>
      </header>

      <main className="inhalt inhalt--seite">
        <h2 className="feld__titel">{ART_TITEL[eintrag.type]}</h2>
        <p className="hinweis">
          {datum(eintrag.datum)}
          {eintrag.beschreibung ? ` · ${eintrag.beschreibung}` : ''}
        </p>

        {eintrag.type === 'trip' && eintrag.status === 'laufend' && (
          <section className="tafel tafel--betont">
            <p className="etikett">Läuft noch</p>
            <p className="hinweis" style={{ marginTop: 0 }}>
              Dieser Ausflug ist gestartet, aber nicht gestoppt. Solange geht er
              in keine Berechnung ein – Beiträge und Saldo stehen erst fest,
              wenn der Zählerstand beim Ankommen erfasst ist.
            </p>
          </section>
        )}

        {r && (
          <section className="tafel tafel--betont">
            {/*
              Oben steht, was der Ausflug gekostet hat - die Zahl, um die es
              geht. Darunter, wer davon was trägt. Die früheren Zeilen "hin
              und zurück" und "nur eine Strecke" sind entfallen: sie waren
              Zwischenergebnisse, und in der Liste steht neben jedem Namen
              ohnehin sein Betrag.
            */}
            <p className="etikett">Preis des Ausflugs</p>
            <div className="beitrag">
              <span className="beitrag__wert">{euro(r.preisAusflug)}</span>
            </div>
            {eintrag.type === 'trip' &&
              (eintrag.mitfahrer?.length ?? 0) > 0 && (
                <>
                  <p className="etikett etikett--zwischen">
                    Beiträge der MitfahrerInnen
                  </p>
                  <Gaesteliste
                    eintrag={eintrag}
                    ergebnis={r}
                    aufAktion={aufAktion}
                  />
                </>
              )}
            {/*
              Für Ausflüge von vor R-24 gibt es keine Liste und damit keine
              Zeile, unter die der Betrag gehörte - dort bleibt er anonym.
            */}
            {r.beitragZahlenderMitfahrer != null && !zahlerInListe && (
              <Zeile
                titel={
                  r.beitragZahlenderMitfahrer < 0
                    ? 'zurück an die Person, die geparkt hat'
                    : 'noch offen von der Person, die geparkt hat'
                }
                wert={euro(Math.abs(r.beitragZahlenderMitfahrer))}
              />
            )}
            {r.ausSnapshot && (
              <p className="hinweis">
                Abgeschlossen – diese Beträge ändern sich nicht mehr.
              </p>
            )}
          </section>
        )}

        <section className="tafel">
          <p className="etikett">Angaben</p>
          <Angaben eintrag={eintrag} />
        </section>

        {r && (
          <section className="tafel">
            <p className="etikett">Berechnung</p>
            <Zeile titel="Gefahrene Kilometer" wert={`${zahl(r.kmGefahren, 1)} km`} />
            <ZeileZahl
              titel="Verbrauch"
              wert={r.verbrauchL100km}
              einheit="l/100 km"
              manuell={r.verbrauchQuelle === 'VM'}
              gesperrt={eintrag.type === 'trip' && eintrag.finalisiert === true}
              aufSpeichern={(v) =>
                aufAktion(async () => {
                  await api.aendere(eintrag.id, {
                    verbrauchL100kmManuell: v,
                  } as Partial<Entry>);
                })
              }
            />
            <ZeileZahl
              titel="Preis pro Liter"
              wert={r.preisProLiter}
              einheit="€"
              manuell={r.preisQuelle === 'PM'}
              gesperrt={eintrag.type === 'trip' && eintrag.finalisiert === true}
              aufSpeichern={(v) =>
                aufAktion(async () => {
                  await api.aendere(eintrag.id, {
                    preisProLiterManuell: v,
                  } as Partial<Entry>);
                })
              }
            />
            <Zeile titel="Kosten pro Kilometer" wert={`${zahl(r.gesamtProKm, 2)} €`} />
            {r.parkgebuehr > 0 && (
              <>
                <Zeile titel="Fahrtkosten" wert={euro(r.fahrtkosten)} />
                <Zeile titel="Parkgebühr" wert={euro(r.parkgebuehr)} />
              </>
            )}
            <Zeile titel="Preis des Ausflugs" wert={euro(r.preisAusflug)} />
            <Zeile
              titel="davon Instandhaltung"
              wert={euro(r.instandhaltungAusflug)}
            />
          </section>
        )}

        {fotos.length > 0 && (
          <section className="tafel">
            <p className="etikett">Fotos</p>
            <div className="fotostreifen">
              {fotos.map((f) => (
                <Foto key={f.id} id={f.id} beschriftung={fotoTitel(f.feld)} />
              ))}
            </div>
          </section>
        )}

        <section className="tafel knopfreihe" style={{ flexDirection: 'column' }}>
          {eintrag.type === 'trip' && eintrag.status === 'laufend' && (
            <button className="knopf" onClick={aufStoppen}>
              Ausflug stoppen
            </button>
          )}
          {eintrag.type === 'trip' && eintrag.status !== 'laufend' && (
            <button
              className="knopf knopf--leise"
              onClick={() =>
                aufAktion(async () => {
                  if (eintrag.finalisiert) await api.oeffneWieder(eintrag.id);
                  else await api.schliesseAb(eintrag.id);
                })
              }
            >
              {eintrag.finalisiert
                ? 'Abschluss wieder lösen'
                : 'Beträge kassiert – Ausflug abschliessen'}
            </button>
          )}
          {/*
            Zu früh gestoppt: der Ausflug läuft dann wieder weiter und geht so
            lange in keine Berechnung ein (R-23). Der Endstand bleibt stehen,
            damit das nächste Stoppen ihn schon im Feld hat.
          */}
          {eintrag.type === 'trip' &&
            eintrag.status !== 'laufend' &&
            !eintrag.finalisiert && (
              <button
                className="knopf knopf--leise"
                onClick={() =>
                  aufAktion(async () => {
                    await api.aendere(eintrag.id, {
                      status: 'laufend',
                    } as Partial<Entry>);
                  })
                }
              >
                Stopp zurücknehmen – Ausflug läuft weiter
              </button>
            )}
          <button
            className="knopf knopf--gefahr"
            onClick={() =>
              aufAktion(async () => {
                if (eintrag.geloescht) await api.stelleWiederHer(eintrag.id);
                else await api.loesche(eintrag.id);
                aufZurueck();
              })
            }
          >
            {eintrag.geloescht ? 'Aus dem Papierkorb holen' : 'In den Papierkorb'}
          </button>

          {eintrag.geloescht && (
            <EndgueltigLoeschen
              aufLoeschen={() =>
                aufAktion(async () => {
                  await api.loescheEndgueltig(eintrag.id);
                  aufZurueck();
                })
              }
            />
          )}
        </section>
      </main>
    </>
  );
}

/**
 * Ist mit dem letzten Häkchen alles kassiert, ist der Ausflug erledigt und
 * wird abgeschlossen (R-7).
 *
 * Bewusst nur in diese Richtung: ein Häkchen wieder wegzunehmen löst den
 * Abschluss nicht von selbst, dafür gibt es den eigenen Knopf. Sonst würde
 * ein Verklicken ein festgeschriebenes Ergebnis wieder aufmachen.
 */
export function schliesstAbschluss(
  liste: readonly Mitfahrer[],
  schonFinalisiert: boolean,
): boolean {
  return (
    !schonFinalisiert && liste.length > 0 && liste.every((m) => m.bezahlt)
  );
}

/**
 * Die MitfahrerInnen eines Ausflugs mit ihren Beträgen (R-24).
 *
 * Die Liste geht in keine Berechnung ein - sie beantwortet die Frage, die
 * sich am Abend stellt: von wem fehlt noch Geld. Das Häkchen bleibt deshalb
 * auch bei einem abgeschlossenen Ausflug (R-7) klickbar; das Ergebnis steht
 * dann fest, das Geld trudelt aber weiter ein.
 */
function Gaesteliste({
  eintrag,
  ergebnis,
  aufAktion,
}: {
  eintrag: TripEntry;
  ergebnis: TripResult;
  aufAktion: (fn: () => Promise<void>) => Promise<void>;
}) {
  const liste = eintrag.mitfahrer ?? [];

  /*
    Was diese Person zu zahlen hat - Einzelstrecken kosten anteilig (R-21).
    Wer das Parken ausgelegt hat, sieht hier gleich den verrechneten Betrag:
    zwei verschiedene Zahlen für dieselbe Person wären nicht zu deuten.
  */
  const betrag = (m: Mitfahrer) =>
    m.id === eintrag.parkgebuehrMitfahrerId &&
    ergebnis.beitragZahlenderMitfahrer != null
      ? ergebnis.beitragZahlenderMitfahrer
      : m.hin && m.rueck
        ? ergebnis.preisPersonGesamt
        : ergebnis.preisEineStrecke;

  function umschalten(id: string) {
    const neu = liste.map((m) =>
      m.id === id ? { ...m, bezahlt: !m.bezahlt } : m,
    );
    void aufAktion(async () => {
      await api.aendere(eintrag.id, { mitfahrer: neu } as Partial<Entry>);
      if (schliesstAbschluss(neu, eintrag.finalisiert === true)) {
        await api.schliesseAb(eintrag.id);
      }
    });
  }

  /*
    Auch Michi und Otto sitzen im Auto und tragen ihren Anteil. Ohne ihre
    Zeilen ergäbe die Spalte weniger als den Ausflugspreis darüber, und es
    sähe nach einem Rechenfehler aus. Ein Häkchen bekommen sie nicht - sie
    kassieren sich nichts selbst.
  */
  const eigene: Person[] =
    eintrag.werUnterwegs === 'beide'
      ? ['michi', 'otto']
      : eintrag.werUnterwegs === 'keiner'
        ? []
        : [eintrag.werUnterwegs as Person];

  /*
    Was die anderen zusammen tragen, auf den Cent genau so, wie es daneben
    steht - und was am Parkautomaten schon gelegt wurde.
  */
  /*
    Hat Michi oder Otto das Parken ausgelegt und fährt selbst mit, gehört ihm
    der Betrag zurück - genau wie einer MitfahrerIn, die auslegt. Vorher stand
    in seiner Zeile der volle Anteil, als hätte er das Parken nicht bezahlt.

    Fährt die auslegende Person gar nicht mit, hat sie hier keine Zeile: Ihre
    Auslage kommt dann allein über den Ausgleich zurück.
  */
  const eigenerZahler =
    eintrag.parkgebuehrVon === 'michi' || eintrag.parkgebuehrVon === 'otto'
      ? eigene.find((wer) => wer === eintrag.parkgebuehrVon)
      : undefined;
  const parkVonEigen = eigenerZahler ? (eintrag.parkgebuehr ?? 0) : 0;

  /*
    Was schon am Automaten gelegt wurde - egal von wem. Es steht als eigene
    Zeile in der Spalte, weil die Beiträge daneben bereits darum gekürzt sind
    und die Summe sonst nicht auf den Ausflugspreis käme.
  */
  const schonGelegt = round2(ergebnis.parkgebuehrVonMitfahrer + parkVonEigen);

  const gastBetrag = liste.map((m) => betrag(m));
  const eigenBetrag = eigene.map((wer) =>
    round2(
      ergebnis.preisPersonGesamt - (wer === eigenerZahler ? parkVonEigen : 0),
    ),
  );

  /*
    Der Rundungsrest landet auf der letzten Zeile, die eine Person trägt.

    Jeden Anteil einzeln zu runden lässt die Spalte sonst um ein, zwei Cent
    neben dem Ausflugspreis landen; wer nachrechnet, findet einen Fehler, wo
    keiner ist. Bevorzugt trifft es Michi oder Otto - sie tragen ohnehin, was
    nach den Beiträgen der anderen übrig bleibt. Fährt keiner von beiden mit,
    bleibt der Cent beim letzten Gast.
  */
  const rest = round2(
    ergebnis.preisAusflug -
      round2(
        [...gastBetrag, ...eigenBetrag].reduce((a, b) => a + b, 0) +
          schonGelegt,
      ),
  );
  if (eigenBetrag.length > 0) {
    const i = eigenBetrag.length - 1;
    eigenBetrag[i] = round2(eigenBetrag[i]! + rest);
  } else if (gastBetrag.length > 0) {
    const i = gastBetrag.length - 1;
    gastBetrag[i] = round2(gastBetrag[i]! + rest);
  }

  /*
    Was am Parkautomaten schon gelegt wurde, steht eingerückt direkt unter
    der Person, die es gelegt hat - dort erklärt sich der gekürzte Betrag
    darüber von selbst. In der Spalte fehlen darf es nicht: die Beiträge
    sind bereits darum gekürzt, und die Summe soll den Ausflugspreis treffen.
  */
  const parkZeile =
    schonGelegt > 0 ? (
      <div className="gast__zeile gast__zeile--eigen gast__zeile--unter">
        <span className="gast__name">Parken</span>
        <span className="wert">{euro(schonGelegt)}</span>
      </div>
    ) : null;

  return (
    <>
      {eigene.map((wer, i) => (
        <Fragment key={wer}>
          <div className="gast__zeile gast__zeile--eigen">
            <span className="gast__name">
              {NAME[wer]}
              <span className="gast__strecke">selbst</span>
            </span>
            <span className="wert">{euro(eigenBetrag[i]!)}</span>
          </div>
          {wer === eigenerZahler && parkZeile}
        </Fragment>
      ))}
      {liste.map((m, i) => (
        <Fragment key={m.id}>
          <label className="gast__zeile">
            <input
              type="checkbox"
              checked={m.bezahlt}
              onChange={() => umschalten(m.id)}
            />
            <span
              className={
                m.bezahlt ? 'gast__name gast__name--bezahlt' : 'gast__name'
              }
            >
              {m.name || 'ohne Namen'}
              {!(m.hin && m.rueck) && (
                <span className="gast__strecke">
                  {m.hin ? 'nur hin' : 'nur zurück'}
                </span>
              )}
            </span>
            <span className="wert">{euro(gastBetrag[i]!)}</span>
          </label>
          {m.id === eintrag.parkgebuehrMitfahrerId && parkZeile}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Endgültiges Löschen aus dem Papierkorb.
 *
 * Zwei Stufen statt eines Systemdialogs: Der erste Druck macht aus dem Knopf
 * die Rückfrage, erst der zweite löscht. Das trifft man nicht versehentlich,
 * und es sieht aus wie der Rest der App. Ein Weg zurück fehlt danach - der
 * Vorgang und seine Fotos sind dann wirklich weg.
 */
function EndgueltigLoeschen({ aufLoeschen }: { aufLoeschen: () => void }) {
  const [sicher, setSicher] = useState(false);

  if (!sicher) {
    return (
      <button className="knopf knopf--leise" onClick={() => setSicher(true)}>
        Endgültig löschen
      </button>
    );
  }

  return (
    <>
      <p className="hinweis hinweis--warnung">
        Der Vorgang und seine Fotos werden unwiderruflich gelöscht.
      </p>
      <div className="knopfreihe" style={{ marginTop: 0 }}>
        <button className="knopf knopf--leise" onClick={() => setSicher(false)}>
          Abbrechen
        </button>
        <button className="knopf knopf--gefahr" onClick={aufLoeschen}>
          Ja, endgültig löschen
        </button>
      </div>
    </>
  );
}

/**
 * Ein Foto aus der API.
 *
 * Der naheliegende Weg - <img src={api.fotoUrl(id)}> - funktioniert nicht:
 * der Browser holt diese Adresse ohne die Kopfzeile X-Baba-Wort, und der
 * Worker antwortet mit 401. Also wird das Bild wie der CSV-Export mit
 * gesetztem Zugangswort geholt und als Objekt-URL angezeigt.
 */
/** Klartext statt Spaltenwert: 'km_start' sagt niemandem etwas. */
function fotoTitel(feld: string | null | undefined): string {
  switch (feld) {
    case 'km_start':
      return 'Tacho beim Losfahren';
    case 'km_ende':
      return 'Tacho beim Ankommen';
    case 'km_stand':
      return 'Zählerstand';
    case 'beleg':
      return 'Beleg';
    default:
      return 'Foto';
  }
}

function Foto({ id, beschriftung }: { id: string; beschriftung: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);

  useEffect(() => {
    let lebt = true;
    let objektUrl: string | null = null;

    api
      .holeFoto(id)
      .then((blob) => {
        if (!lebt) return;
        objektUrl = URL.createObjectURL(blob);
        setUrl(objektUrl);
      })
      .catch(() => {
        if (lebt) setFehlgeschlagen(true);
      });

    return () => {
      lebt = false;
      if (objektUrl) URL.revokeObjectURL(objektUrl);
    };
  }, [id]);

  /*
    Die Beschriftung steht sichtbar unter dem Bild, nicht nur im alt-Text:
    Bei drei Fotos an einem Ausflug - Tacho beim Losfahren, Tacho beim
    Ankommen, Beleg - sieht man den Daumennägeln sonst nicht an, welches
    welches ist.
  */
  return (
    <figure className="fotokachel">
      {fehlgeschlagen ? (
        <span className="foto foto--fehlt">nicht ladbar</span>
      ) : !url ? (
        <span className="foto foto--laedt" aria-hidden="true" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={beschriftung} />
        </a>
      )}
      <figcaption>{beschriftung}</figcaption>
    </figure>
  );
}

/**
 * Was im Vorgang eingetragen wurde. Bis hierher zeigte die Detailansicht nur
 * die Berechnung eines Ausflugs - bei Tanken, Reparatur und Ausgleich blieb
 * sie damit leer.
 */
function Angaben({ eintrag }: { eintrag: Entry }) {
  switch (eintrag.type) {
    case 'trip':
      return (
        <>
          <Zeile
            titel="Wer ist unterwegs"
            wert={WER_UNTERWEGS[eintrag.werUnterwegs]}
          />
          {eintrag.werUnterwegs === 'beide' && eintrag.kassierer && (
            <Zeile titel="Wer kassiert" wert={NAME[eintrag.kassierer]} />
          )}
          {eintrag.kmStart != null && (
            <Zeile titel="Kilometerstand Start" wert={km(eintrag.kmStart)} />
          )}
          {eintrag.kmEnde != null && (
            <Zeile
              titel="Kilometerstand Ende"
              wert={km(eintrag.kmEnde)}
            />
          )}
          {/*
            Ein übernommener Endstand ist abgeleitet, nicht abgelesen (R-26).
            Das gehört dazugesagt - sonst sieht er aus wie eine Messung.
          */}
          {eintrag.kmEndeUebernommen === true && (
            <p className="hinweis">
              Der Endstand wurde automatisch vom nächsten Vorgang übernommen,
              weil dieser Ausflug nie gestoppt wurde.
            </p>
          )}
          {eintrag.kmGefahren != null && (
            <Zeile titel="Gefahrene Kilometer" wert={km(eintrag.kmGefahren)} />
          )}
          {/*
            "MitfahrerInnen hin/zurück" stehen hier nicht mehr: sie ergeben
            sich aus der Liste und ihren Häkchen, und eine zweite Anzeige
            derselben Angabe kann nur widersprechen.
          */}
          {eintrag.namenMitfahrer && (
            <Zeile titel="Namen" wert={eintrag.namenMitfahrer} />
          )}
          <Zeile
            titel="Zusatzkosten pro km"
            wert={`${zahl(eintrag.zusatzkostenProKm, 2)} €`}
          />
          {eintrag.parkgebuehr != null && (
            <>
              <Zeile titel="Parkgebühr" wert={euro(eintrag.parkgebuehr)} />
              {/* Eigene Zeile: Betrag und Name zusammen sprengten die Breite. */}
              <Zeile
                titel="Parken bezahlt von"
                wert={
                  eintrag.parkgebuehrVon === 'mitfahrer'
                    ? (eintrag.mitfahrer?.find(
                        (m) => m.id === eintrag.parkgebuehrMitfahrerId,
                      )?.name ?? 'MitfahrerIn')
                    : NAME[eintrag.parkgebuehrVon as Person]
                }
              />
            </>
          )}
        </>
      );

    case 'refuel':
      return (
        <>
          <Zeile titel="Bezahlt von" wert={NAME[eintrag.bezahltVon]} />
          <Zeile titel="Betrag" wert={euro(eintrag.betrag)} />
          <Zeile titel="Getankte Liter" wert={`${zahl(eintrag.liter, 2)} l`} />
          <Zeile
            titel="Preis pro Liter"
            wert={
              eintrag.liter > 0
                ? `${zahl(eintrag.betrag / eintrag.liter, 2)} €`
                : '–'
            }
          />
          <Zeile titel="Kilometerstand" wert={km(eintrag.kmStand)} />
          <Zeile
            titel="Vollgetankt"
            wert={eintrag.volltankung ? 'ja' : 'nein'}
          />
        </>
      );

    case 'repair':
      return (
        <>
          <Zeile titel="Bezahlt von" wert={NAME[eintrag.bezahltVon]} />
          <Zeile titel="Betrag" wert={euro(eintrag.betrag)} />
          {eintrag.kmStand != null && (
            <Zeile titel="Kilometerstand" wert={km(eintrag.kmStand)} />
          )}
        </>
      );

    case 'settlement':
      return (
        <>
          <Zeile
            titel="Richtung"
            wert={
              eintrag.richtung === 'michi_an_otto'
                ? `${NAME.michi} an ${NAME.otto}`
                : `${NAME.otto} an ${NAME.michi}`
            }
          />
          <Zeile titel="Betrag" wert={euro(eintrag.betrag)} />
        </>
      );

    case 'fixed_cost':
      return (
        <>
          <Zeile titel="Bezahlt von" wert={NAME[eintrag.bezahltVon]} />
          <Zeile titel="Betrag" wert={euro(eintrag.betrag)} />
          {eintrag.kategorie && (
            <Zeile titel="Kategorie" wert={eintrag.kategorie} />
          )}
        </>
      );
  }
}

function Zeile({ titel, wert }: { titel: string; wert: string }) {
  return (
    <div className="zeile">
      <span>{titel}</span>
      <span className="wert">{wert}</span>
    </div>
  );
}

/**
 * Eine Wertezeile, deren Zahl sich ändern lässt - für Verbrauch und
 * Literpreis eines Ausflugs.
 *
 * Die Zeile sieht aus wie jede andere; nur die Zahl selbst ist ein Feld.
 * Leeren und verlassen setzt den Wert zurück auf "automatisch", dann greifen
 * wieder die Regeln aus den Tankvorgängen.
 *
 * Bei einem abgeschlossenen Ausflug (R-7) bleibt die Zeile unveränderlich:
 * das Ergebnis stammt dort aus dem Snapshot, eine Eingabe hier bliebe
 * wirkungslos und wäre damit irreführend.
 */
function ZeileZahl({
  titel,
  wert,
  einheit,
  manuell,
  gesperrt,
  aufSpeichern,
}: {
  titel: string;
  wert: number;
  einheit: string;
  /** Wurde der Wert von Hand gesetzt? Dann bekommt die Zeile ein Zeichen. */
  manuell: boolean;
  gesperrt: boolean;
  aufSpeichern: (wert: number | null) => Promise<void>;
}) {
  const gerundet = zuText(wert, 2);
  const [text, setText] = useState(gerundet);

  // Nach dem Speichern rechnet die App neu; die Zeile übernimmt das Ergebnis.
  useEffect(() => setText(gerundet), [gerundet]);

  if (gesperrt) {
    return (
      <Zeile
        titel={titel}
        wert={`${gerundet} ${einheit}${manuell ? ' · manuell' : ''}`}
      />
    );
  }

  function uebernehmen() {
    const roh = text.trim();
    if (roh === gerundet) return;
    if (roh === '') {
      void aufSpeichern(null);
      return;
    }
    const neu = zuZahl(roh);
    if (neu == null || neu <= 0) {
      setText(gerundet);
      return;
    }
    void aufSpeichern(neu);
  }

  return (
    <div className="zeile zeile--eingabe">
      <label className="zeile__titel" htmlFor={`wert-${titel}`}>
        {titel}
      </label>
      <span className="wert">
        <input
          id={`wert-${titel}`}
          className="wert__eingabe"
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={uebernehmen}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
        {einheit}
        {manuell && <span className="handzeichen">manuell</span>}
      </span>
    </div>
  );
}
